/** @jsx jsx */

import type { WithDataAttributeProp } from '@balance-web/core';
import { getTestIdFromDataAttributes, jsx } from '@balance-web/core';
import { HelpCircleIcon } from '@balance-web/icon/icons/HelpCircleIcon';
import { useTheme } from '@balance-web/theme';
import { Tooltip } from '@balance-web/tooltip';
import type { TooltipProps } from '@balance-web/tooltip';
import { useContext, useMemo } from 'react';
import { makeIdFactory } from '@balance-web/utils';

import { FieldContext } from './context';

const FieldHelpIdFactory = makeIdFactory('field-help');

type HelpIndicatorProps = WithDataAttributeProp<{
  /** The ID of the element. */
  id?: string;
  /** The content of the tooltip. */
  content: TooltipProps['content'];
  placement?: 'top' | 'right' | 'bottom' | 'left';
}>;

export const HelpIndicator = (props: HelpIndicatorProps) => {
  const { spacing } = useTheme();
  const testids = FieldHelpIdFactory();
  const fieldContext = useContext(FieldContext);
  const testid = useMemo(() => {
    const existing = getTestIdFromDataAttributes(props.data);
    if (existing) {
      return existing;
    }

    if (props.id) {
      return testids.base(props.id);
    }

    return testids.base(fieldContext?.id);
  }, []);

  return (
    <Tooltip
      content={props.content}
      data={{ testid }}
      data-balance-component="field-help-indicator"
      id={props.id}
      placement={props.placement}
    >
      <span
        aria-label="Help"
        css={{
          alignItems: 'center',
          background: 0,
          border: 0,
          cursor: 'help',
          display: 'flex',
          justifyContent: 'center',
          marginLeft: spacing.xsmall,
          padding: 0,
          position: 'relative',

          // increase hit target
          '::before': {
            bottom: -spacing.xsmall,
            content: '" "',
            left: -spacing.xsmall,
            position: 'absolute',
            right: -spacing.xsmall,
            top: -spacing.xsmall,
          },
        }}
      >
        <HelpCircleIcon color="dim" size="small" />
      </span>
    </Tooltip>
  );
};
